/**
 * Estimates list screen (WO-M4).
 *
 * Lists EstimateSummary rows for the authenticated org. Tapping a row navigates
 * to the estimate detail screen at /(app)/estimates/[id].
 *
 * Displayed fields per row: customer (customerId), status, approval_status, total.
 * customerID is an opaque UUID; no PII is shown in the URL (mobile CLAUDE.md §2).
 *
 * WO-M0 refactor — rebuilt on the design system (Screen / ListRow / Badge / Text + tokens).
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { listEstimates } from '../../../src/api/billing';
import { ApiRequestError } from '../../../src/api/client';
import { Screen, Text, Badge, Card, Button, Field, ListRow, estimateBadge } from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { filterEstimates } from '../../../src/lib/listSearch';
import type { EstimateSummary } from '@beam/api-types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: string | null | undefined, currency: string): string {
  if (!value) return '—';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(num);
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Failed to load estimates.';
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function EstimatesScreen() {
  const router = useRouter();
  const [query, setQuery] = useState('');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['estimates'],
    queryFn: () => listEstimates(),
    staleTime: 30_000,
  });

  // Client-side filter of already-loaded rows (no extra fetch). Matches the
  // VISIBLE short id (id.slice(0,8)), status and approval status — see
  // src/lib/listSearch (searching the full UUID over-matched the hidden tail).
  const filtered = useMemo(() => (data ? filterEstimates(data, query) : data), [data, query]);

  if (isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading estimates"
          />
          <Text variant="body" color="secondary">
            Loading estimates…
          </Text>
        </View>
      </Screen>
    );
  }

  if (isError) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(error)}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => refetch()}
              accessibilityLabel="Retry loading estimates"
            />
          </Card>
        </View>
      </Screen>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Text variant="title">No estimates yet</Text>
          <Text variant="body" color="secondary" style={styles.emptySubtitle}>
            Create your first estimate to get started.
          </Text>
          <Button
            title="+ New estimate"
            fullWidth={false}
            onPress={() => router.push('/(app)/estimates/new')}
            accessibilityLabel="Create a new estimate"
          />
        </View>
      </Screen>
    );
  }

  const rows = filtered ?? [];

  return (
    <Screen scroll>
      <Button
        title="+ New estimate"
        onPress={() => router.push('/(app)/estimates/new')}
        accessibilityLabel="Create a new estimate"
      />
      <Field
        placeholder="Search estimates"
        value={query}
        onChangeText={setQuery}
        autoCapitalize="none"
        autoCorrect={false}
        returnKeyType="search"
        clearButtonMode="while-editing"
        accessibilityLabel="Search estimates"
      />
      <Text variant="label" color="secondary">
        {rows.length} {rows.length === 1 ? 'estimate' : 'estimates'}
      </Text>
      {rows.length === 0 ? (
        <Text variant="body" color="secondary">
          No estimates match “{query.trim()}”.
        </Text>
      ) : null}
      <View style={styles.list}>
        {rows.map((estimate: EstimateSummary, index: number) => {
          const b = estimateBadge(estimate.approvalStatus ?? estimate.status);
          const amount = formatCurrency(estimate.total, estimate.currency);
          const dateStr = estimate.issuedDate
            ? `Issued ${new Date(estimate.issuedDate).toLocaleDateString()}`
            : estimate.status;
          return (
            <ListRow
              key={estimate.id}
              testID={`estimate-row-${index}`}
              title={estimate.id.slice(0, 8).toUpperCase()}
              subtitle={dateStr}
              right={
                <View style={styles.rightSlot}>
                  <Text variant="data">{amount}</Text>
                  <Badge status={b.key} label={b.label} />
                </View>
              }
              onPress={() => router.push(`/(app)/estimates/${estimate.id}`)}
            />
          );
        })}
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  emptySubtitle: {
    textAlign: 'center',
  },
  list: {
    gap: theme.spacing.sm,
  },
  rightSlot: {
    alignItems: 'flex-end',
    gap: theme.spacing.xs,
  },
});
